/**
 * Medication List Page
 *
 * eMAR workflow for medication support, PRN protocols, safety checks,
 * refused/held/missed records and MAR audit history.
 */

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  Bell,
  CalendarDays,
  CheckCircle2,
  Clock,
  ClipboardCheck,
  Download,
  FileText,
  History,
  Pill,
  Printer,
  ShieldCheck,
  Thermometer,
  UserCheck,
  XCircle,
} from 'lucide-react';
import { localMedications } from '@/core/services/local-api-fallback';
import { getLocalStorage, setLocalStorage } from '@/utils/localStorage';

type MedicationStatus = 'ACTIVE' | 'DISCONTINUED' | 'ON_HOLD';
type AdministrationStatus = 'GIVEN' | 'REFUSED' | 'HELD' | 'MISSED';
type MedicationRoute = 'ORAL' | 'TOPICAL' | 'INJECTION' | 'INHALATION' | 'OTHER';
type MedicationTab = 'board' | 'rounds' | 'stock' | 'schedule' | 'prn' | 'audit' | 'escalations' | 'report';
type ApprovalStatus = 'PENDING_APPROVAL' | 'REVIEWED' | 'REJECTED';
type ScheduleStatus = 'DUE' | 'OVERDUE' | 'CONFIRMED' | 'PRN';

interface Medication {
  id: string;
  clientId: string;
  medicationName: string;
  genericName?: string;
  dosage: string;
  route: MedicationRoute;
  frequency: string;
  instructions?: string;
  prescribedBy: string;
  prescribedDate: string;
  startDate: string;
  endDate?: string;
  status: MedicationStatus;
  name?: string;
  prn?: boolean;
  allergyWarning?: string;
  createdAt?: string;
  highRisk?: boolean;
  requiresDoubleCheck?: boolean;
  refillsRemaining?: number;
  lastAdministered?: string;
  nextScheduledTime?: string;
  sideEffects?: string[];
  warnings?: string[];
  prnProtocol?: string;
  maxDailyDose?: string;
  safetyChecks?: string[];
}

interface MedicationAdministration {
  id: string;
  medicationId: string;
  clientId: string;
  medicationName: string;
  administeredBy: string;
  administeredAt: string;
  dosageGiven: string;
  status: AdministrationStatus;
  notes?: string;
  refusalReason?: string;
  prnReason?: string;
  painScore?: string;
  bodyTemperature?: string;
  effectivenessReview?: string;
  followUpDueAt?: string;
  followUpStatus?: 'PENDING' | 'COMPLETED' | '';
  requiresDoubleCheck?: boolean;
  doubleCheckBy?: string;
  signedBy?: string;
  signedAt?: string;
}

interface MedicationScheduleItem {
  id: string;
  medicationId: string;
  medicationName: string;
  dose: string;
  route: MedicationRoute;
  dueAt: string;
  status: ScheduleStatus;
  instruction: string;
}

interface MedicationEscalation {
  id: string;
  medicationId: string;
  medicationName: string;
  clientId: string;
  reason: string;
  priority: 'HIGH' | 'MEDIUM';
  createdAt: string;
  dueAt: string;
  owner: string;
  status: ApprovalStatus;
  emarRecordId?: string;
  reviewedBy?: string;
  reviewedAt?: string;
}

interface MedicationRound {
  id: string;
  clientId: string;
  name: string;
  windowStart: string;
  windowEnd: string;
  medicationIds: string[];
  status: 'DUE' | 'COMPLETED' | 'MISSED' | 'PRN' | 'DOUBLE_CHECK_REQUIRED';
}

interface MedicationStockItem {
  id: string;
  medicationId: string;
  clientId: string;
  medicationName: string;
  quantity: number;
  unit: string;
  reorderLevel: number;
  lastCountedAt?: string;
  countedBy?: string;
  status: 'IN_STOCK' | 'LOW_STOCK' | 'COUNT_REQUIRED';
}

interface MedicationSignature {
  id: string;
  medicationId: string;
  clientId: string;
  signedBy: string;
  role: string;
  signedAt: string;
  doubleCheckBy?: string;
}

interface MedicationProductionSafety {
  rounds: MedicationRound[];
  stock: MedicationStockItem[];
  escalations: MedicationEscalation[];
  signatures: MedicationSignature[];
  records: MedicationAdministration[];
}

interface HumanApprovalItem {
  id: string;
  medicationId: string;
  medicationName: string;
  decision: AdministrationStatus;
  reason: string;
  requestedBy: string;
  createdAt: string;
  status: ApprovalStatus;
  reviewedBy?: string;
  reviewedAt?: string;
}

interface MedicationSafetyProfile {
  clientName: string;
  allergies: string[];
  highRiskNotes: string[];
}

interface AdministrationForm {
  status: AdministrationStatus;
  prnReason: string;
  painScore: string;
  bodyTemperature: string;
  refusalReason: string;
  notes: string;
  effectivenessReview: string;
  followUpDueMinutes: string;
  signatureName: string;
  signatureRole: string;
  signatureDeclaration: boolean;
  doubleCheckBy: string;
  doubleCheckSignature: string;
  checkedIdentity: boolean;
  checkedMedicine: boolean;
  checkedDose: boolean;
  checkedRoute: boolean;
  checkedTime: boolean;
  checkedWarnings: boolean;
}

async function fetchMedications(clientId: string): Promise<Medication[]> {
  if (import.meta.env.VITE_LOCAL_DATA_MODE === 'true') {
    return localMedications.filter((medication) => medication.clientId === clientId) as Medication[];
  }

  const response = await fetch(`/api/clients/${clientId}/medications`, {
    credentials: 'include',
  });
  if (!response.ok) throw new Error('Failed to fetch medications');
  return response.json();
}

async function fetchMedicationProductionSafety(clientId: string): Promise<MedicationProductionSafety> {
  if (import.meta.env.VITE_LOCAL_DATA_MODE === 'true') {
    return { rounds: [], stock: [], escalations: [], signatures: [], records: [] };
  }

  const response = await fetch(`/api/clients/${clientId}/medication-safety`, {
    credentials: 'include',
  });
  if (!response.ok) throw new Error('Failed to fetch medication safety controls');
  return response.json();
}

const initialForm: AdministrationForm = {
  status: 'GIVEN',
  prnReason: '',
  painScore: '',
  bodyTemperature: '',
  refusalReason: '',
  notes: '',
  effectivenessReview: '',
  followUpDueMinutes: '60',
  signatureName: '',
  signatureRole: 'Carer',
  signatureDeclaration: false,
  doubleCheckBy: '',
  doubleCheckSignature: '',
  checkedIdentity: false,
  checkedMedicine: false,
  checkedDose: false,
  checkedRoute: false,
  checkedTime: false,
  checkedWarnings: false,
};

const safetyFields: Array<keyof Pick<
  AdministrationForm,
  | 'checkedIdentity'
  | 'checkedMedicine'
  | 'checkedDose'
  | 'checkedRoute'
  | 'checkedTime'
  | 'checkedWarnings'
>> = [
  'checkedIdentity',
  'checkedMedicine',
  'checkedDose',
  'checkedRoute',
  'checkedTime',
  'checkedWarnings',
];

const safetyLabels: Record<(typeof safetyFields)[number], string> = {
  checkedIdentity: 'Right person confirmed',
  checkedMedicine: 'Right medicine checked',
  checkedDose: 'Right dose checked',
  checkedRoute: 'Right route checked',
  checkedTime: 'Right time checked',
  checkedWarnings: 'Warnings and allergies reviewed',
};

const statusStyles: Record<AdministrationStatus, string> = {
  GIVEN: 'bg-emerald-100 text-emerald-800',
  REFUSED: 'bg-rose-100 text-rose-800',
  HELD: 'bg-amber-100 text-amber-800',
  MISSED: 'bg-red-100 text-red-800',
};

const MAR_RECORDS_KEY = 'fuchsia:emar:mar-records';
const MAR_ESCALATIONS_KEY = 'fuchsia:emar:escalations';
const MAR_APPROVALS_KEY = 'fuchsia:emar:human-approvals';

const medicationSafetyProfiles: Record<string, MedicationSafetyProfile> = {
  'client-1': {
    clientName: 'Margaret Williams',
    allergies: ['Penicillin', 'Latex'],
    highRiskNotes: [
      'Requires medication support prompts and clear MAR evidence.',
      'Family must be informed after missed or refused medication support.',
    ],
  },
  'client-2': {
    clientName: 'George Taylor',
    allergies: ['Aspirin'],
    highRiskNotes: ['Monitor dizziness and hydration before morning medicines.'],
  },
  'client-3': {
    clientName: 'Eileen Cooper',
    allergies: ['Sulfonamides'],
    highRiskNotes: ['Dementia care plan requires calm prompting and family-safe notes.'],
  },
};

const fallbackSafetyProfile: MedicationSafetyProfile = {
  clientName: 'Client',
  allergies: ['No allergy record confirmed in this local preview'],
  highRiskNotes: ['Verify allergies, GP authorisation and MAR chart before production use.'],
};

function getEnhancedMedicationList(clientId: string | undefined, currentMedications: Medication[]) {
  const normalizedMedications = currentMedications.map((medication) => ({
    ...medication,
    medicationName: medication.medicationName || medication.name || 'Medication',
    route: medication.route || 'ORAL',
    prescribedBy: medication.prescribedBy || 'GP / pharmacy record',
    prescribedDate: medication.prescribedDate || medication.createdAt || new Date().toISOString(),
    startDate: medication.startDate || medication.createdAt || new Date().toISOString(),
    status: medication.status || 'ACTIVE',
    prnProtocol: medication.prnProtocol || (medication.prn ? 'PRN protocol must be followed and effectiveness reviewed after administration.' : undefined),
    warnings: medication.warnings || (medication.allergyWarning ? [medication.allergyWarning] : undefined),
  })).map((medication) => {
    const highRiskByRule = ['warfarin', 'insulin', 'morphine', 'methotrexate', 'digoxin', 'aspirin'].some((name) =>
      medication.medicationName.toLowerCase().includes(name)
    );
    return {
      ...medication,
      highRisk: Boolean(medication.highRisk || medication.allergyWarning || highRiskByRule),
      requiresDoubleCheck: Boolean(medication.requiresDoubleCheck || medication.allergyWarning || highRiskByRule),
    };
  });

  if (!clientId) return normalizedMedications;

  const defaultItems: Medication[] = [
    {
      id: `${clientId}-med-ramipril-morning`,
      clientId,
      medicationName: 'Ramipril',
      genericName: 'Ramipril',
      dosage: '5 mg',
      route: 'ORAL',
      frequency: 'Once daily at 08:30',
      instructions: 'Support with water after breakfast. Record blood pressure concern if dizziness is reported.',
      prescribedBy: 'Dr. James Harris',
      prescribedDate: '2026-04-20',
      startDate: '2026-05-01',
      status: 'ACTIVE',
      refillsRemaining: 3,
      lastAdministered: dateHoursFromNow(-24),
      nextScheduledTime: dateHoursFromNow(-1),
      warnings: ['Check dizziness or light-headedness before support.'],
      safetyChecks: ['Confirm identity', 'Confirm MAR entry', 'Record concerns before completing the visit'],
    },
    {
      id: `${clientId}-med-paracetamol-prn`,
      clientId,
      medicationName: 'Paracetamol',
      genericName: 'Paracetamol',
      dosage: '500 mg',
      route: 'ORAL',
      frequency: 'PRN, max four times daily',
      instructions: 'Only support when PRN criteria are met and reason is recorded.',
      prescribedBy: 'Dr. Olivia Smith',
      prescribedDate: '2026-04-20',
      startDate: '2026-05-01',
      status: 'ACTIVE',
      refillsRemaining: 1,
      lastAdministered: dateHoursFromNow(-18),
      nextScheduledTime: dateHoursFromNow(2),
      warnings: ['Check total daily dose and other paracetamol-containing products.'],
      prnProtocol: 'Only support for pain or temperature above 38C. Record reason, pain score, time, dose and effectiveness review after 60 minutes.',
      maxDailyDose: '4,000 mg in 24 hours',
      safetyChecks: [
        'Confirm no other paracetamol-containing product was taken',
        'Escalate if pain remains severe after one hour',
        'Record body temperature when given for fever',
      ],
    },
    {
      id: `${clientId}-med-donepezil-night`,
      clientId,
      medicationName: 'Donepezil',
      genericName: 'Donepezil hydrochloride',
      dosage: '10 mg',
      route: 'ORAL',
      frequency: 'Once nightly',
      instructions: 'Support at bedtime and monitor nausea or sleep disturbance.',
      prescribedBy: 'Dr. Priya Nair',
      prescribedDate: '2026-04-24',
      startDate: '2026-05-02',
      status: 'ACTIVE',
      refillsRemaining: 2,
      lastAdministered: dateHoursFromNow(-20),
      nextScheduledTime: dateHoursFromNow(5),
      sideEffects: ['Nausea', 'Sleep disturbance'],
      warnings: ['Time-sensitive evening medicine. Escalate if not confirmed.'],
      highRisk: true,
      requiresDoubleCheck: true,
      safetyChecks: ['Confirm bedtime schedule', 'Record any nausea or confusion', 'Check care plan communication notes'],
    },
    {
      id: `${clientId}-med-atorvastatin-evening`,
      clientId,
      medicationName: 'Atorvastatin',
      genericName: 'Atorvastatin calcium',
      dosage: '20 mg',
      route: 'ORAL',
      frequency: 'Once daily after evening meal',
      instructions: 'Support after meal; record if meal was refused.',
      prescribedBy: 'Dr. Thomas Reed',
      prescribedDate: '2026-04-28',
      startDate: '2026-05-03',
      status: 'ACTIVE',
      refillsRemaining: 4,
      lastAdministered: dateHoursFromNow(-21),
      nextScheduledTime: dateHoursFromNow(3),
      warnings: ['Escalate unusual muscle pain or weakness.'],
      safetyChecks: ['Confirm evening meal was offered', 'Record refusal or side effect concern'],
    },
    {
      id: `${clientId}-med-amoxicillin-hold`,
      clientId,
      medicationName: 'Amoxicillin',
      genericName: 'Amoxicillin',
      dosage: '250 mg',
      route: 'ORAL',
      frequency: 'On hold',
      instructions: 'Do not support while allergy warning is active. Requires clinician review.',
      prescribedBy: 'Dr. James Harris',
      prescribedDate: '2026-05-10',
      startDate: '2026-05-10',
      status: 'ON_HOLD',
      refillsRemaining: 0,
      warnings: ['Penicillin allergy recorded. Human clinical review required before any change.'],
      safetyChecks: ['Do not restart without authorised clinical instruction', 'Record allergy review evidence'],
    },
  ];

  const existingNames = new Set(normalizedMedications.map((item) => item.medicationName.toLowerCase()));
  const missingDefaults = defaultItems.filter((item) => !existingNames.has(item.medicationName.toLowerCase()));
  return [...normalizedMedications, ...missingDefaults];
}

export function MedicationListPage() {
  const { clientId } = useParams<{ clientId: string }>();
  const [filterStatus, setFilterStatus] = useState<'ALL' | MedicationStatus>('ACTIVE');
  const [activeTab, setActiveTab] = useState<MedicationTab>('board');
  const [selectedMedicationId, setSelectedMedicationId] = useState<string | null>(null);
  const [form, setForm] = useState<AdministrationForm>(initialForm);
  const [isSavingRecord, setIsSavingRecord] = useState(false);
  const [recordError, setRecordError] = useState('');
  const [followUpDrafts, setFollowUpDrafts] = useState<Record<string, { effectivenessReview: string; signatureName: string }>>({});
  const [administrations, setAdministrations] = useState<MedicationAdministration[]>(() =>
    getLocalStorage<MedicationAdministration[]>(MAR_RECORDS_KEY, [])
  );
  const [escalations, setEscalations] = useState<MedicationEscalation[]>(() =>
    getLocalStorage<MedicationEscalation[]>(MAR_ESCALATIONS_KEY, [])
  );
  const [approvals, setApprovals] = useState<HumanApprovalItem[]>(() =>
    getLocalStorage<HumanApprovalItem[]>(MAR_APPROVALS_KEY, [])
  );

  const { data: medications = [], isLoading, error } = useQuery({
    queryKey: ['medications', clientId],
    queryFn: () => fetchMedications(clientId!),
    enabled: !!clientId,
    refetchInterval: 60000,
  });

  const { data: productionSafety, refetch: refetchProductionSafety } = useQuery({
    queryKey: ['medication-production-safety', clientId],
    queryFn: () => fetchMedicationProductionSafety(clientId!),
    enabled: !!clientId,
    refetchInterval: 60000,
  });

  const medicationData = useMemo(
    () => getEnhancedMedicationList(clientId, medications),
    [clientId, medications]
  );
  const safetyProfile = medicationSafetyProfiles[clientId ?? ''] ?? fallbackSafetyProfile;
  const selectedMedication = medicationData.find((medication) => medication.id === selectedMedicationId) ?? null;
  const filteredMedications =
    filterStatus === 'ALL'
      ? medicationData
      : medicationData.filter((medication) => medication.status === filterStatus);

  const generatedMarRecords = useMemo<MedicationAdministration[]>(() => {
    return medicationData.slice(0, 4).map((medication, index) => ({
      id: `local-mar-${medication.id}-${index}`,
      medicationId: medication.id,
      clientId: medication.clientId,
      medicationName: medication.medicationName,
      administeredBy: index === 2 ? 'Amelia Patel' : 'Adrian Popescu',
      administeredAt: `${new Date(Date.now() - (index + 1) * 6 * 60 * 60 * 1000).toISOString()}`,
      dosageGiven: medication.dosage,
      status: index === 2 ? 'MISSED' : index === 3 ? 'REFUSED' : 'GIVEN',
      notes: index === 2 ? 'Dose not available during visit. Coordinator notified.' : 'Recorded in local eMAR.',
      refusalReason: index === 3 ? 'Client declined and requested review later.' : undefined,
      prnReason: medication.prnProtocol ? 'Mild pain reported before lunch.' : undefined,
      painScore: medication.prnProtocol ? '4' : undefined,
      effectivenessReview: medication.prnProtocol ? 'Review due after 60 minutes.' : undefined,
    }));
  }, [medicationData]);

  const serverMarRecords = productionSafety?.records ?? [];
  const serverEscalations = productionSafety?.escalations ?? [];
  const medicationRounds = productionSafety?.rounds ?? buildMedicationRounds(medicationData);
  const medicationStock = productionSafety?.stock ?? buildMedicationStock(medicationData);
  const recentSignatures = productionSafety?.signatures ?? [];
  const marRecords = [...administrations, ...serverMarRecords, ...generatedMarRecords].sort(
    (a, b) => new Date(b.administeredAt).getTime() - new Date(a.administeredAt).getTime()
  );

  const medicationSchedule = useMemo(
    () => buildMedicationSchedule(medicationData, marRecords),
    [medicationData, marRecords]
  );
  const allEscalations = [...serverEscalations, ...escalations];
  const openEscalations = allEscalations.filter((item) => item.status === 'PENDING_APPROVAL');
  const pendingApprovals = approvals.filter((item) => item.status === 'PENDING_APPROVAL');
  const pendingPrnFollowUps = marRecords.filter((record) => record.followUpStatus === 'PENDING' || (record.followUpDueAt && !record.effectivenessReview));
  const complianceRate = calculateMedicationCompliance(marRecords);
  const overdueCount = medicationData.filter((medication) => isOverdue(medication)).length;
  const prnCount = medicationData.filter((medication) => Boolean(medication.prnProtocol)).length;
  const warningCount = medicationData.filter((medication) => (medication.warnings?.length ?? 0) > 0).length;
  const highRiskCount = medicationData.filter((medication) => medication.highRisk || medication.requiresDoubleCheck).length;
  const lowStockCount = medicationStock.filter((item) => item.status !== 'IN_STOCK' || Number(item.quantity) <= Number(item.reorderLevel)).length;
  const missedCount = marRecords.filter((record) => record.status === 'MISSED').length;
  const allergyWarningCount = medicationData.filter((medication) =>
    safetyProfile.allergies.some((allergy) =>
      medication.medicationName.toLowerCase().includes(allergy.toLowerCase()) ||
      (medication.warnings ?? []).some((warning) => warning.toLowerCase().includes(allergy.toLowerCase()))
    )
  ).length;
  const reportStats = buildMedicationReportStats(marRecords, medicationData, allEscalations, approvals);

  const allSafetyChecked = safetyFields.every((field) => form[field]);
  const prnRequired = selectedMedication?.prnProtocol && form.status === 'GIVEN';
  const prnComplete = !prnRequired || (form.prnReason.trim() && form.painScore.trim() && form.followUpDueMinutes.trim());
  const refusalComplete = form.status !== 'REFUSED' || form.refusalReason.trim();
  const doubleCheckRequired = Boolean(selectedMedication?.highRisk || selectedMedication?.requiresDoubleCheck) && form.status === 'GIVEN';
  const doubleCheckComplete = !doubleCheckRequired || (form.doubleCheckBy.trim() && form.doubleCheckSignature.trim());
  const signatureComplete = Boolean(form.signatureName.trim() && form.signatureDeclaration);
  const canRecord = Boolean(selectedMedication) && allSafetyChecked && prnComplete && refusalComplete && doubleCheckComplete && signatureComplete;

  const openAdministration = (medicationId: string, status: AdministrationStatus = 'GIVEN') => {
    setSelectedMedicationId(medicationId);
    setForm({ ...initialForm, status });
  };

  const recordAdministration = async () => {
    if (!selectedMedication || !clientId || !canRecord) return;

    const now = new Date();
    const followUpDueAt = selectedMedication.prnProtocol && form.status === 'GIVEN'
      ? new Date(now.getTime() + Number(form.followUpDueMinutes || 60) * 60 * 1000).toISOString()
      : undefined;

    setIsSavingRecord(true);
    setRecordError('');

    const draftRecord: MedicationAdministration = {
      id: `admin-${Date.now()}`,
      medicationId: selectedMedication.id,
      clientId,
      medicationName: selectedMedication.medicationName,
      administeredBy: form.signatureName,
      administeredAt: now.toISOString(),
      dosageGiven: selectedMedication.dosage,
      status: form.status,
      notes: form.notes || undefined,
      refusalReason: form.refusalReason || undefined,
      prnReason: form.prnReason || undefined,
      painScore: form.painScore || undefined,
      bodyTemperature: form.bodyTemperature || undefined,
      effectivenessReview: form.effectivenessReview || undefined,
      followUpDueAt,
      followUpStatus: followUpDueAt ? 'PENDING' : '',
      requiresDoubleCheck: doubleCheckRequired,
      doubleCheckBy: form.doubleCheckBy || undefined,
      signedBy: form.signatureName,
      signedAt: now.toISOString(),
    };

    let newRecord = draftRecord;

    if (import.meta.env.VITE_LOCAL_DATA_MODE !== 'true') {
      try {
        const response = await fetch(`/api/medications/${selectedMedication.id}/administer`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...draftRecord,
            signatureName: form.signatureName,
            signatureRole: form.signatureRole,
            signatureDeclaration: form.signatureDeclaration,
            doubleCheckBy: form.doubleCheckBy,
            doubleCheckSignature: form.doubleCheckSignature,
            followUpDueMinutes: Number(form.followUpDueMinutes || 60),
          }),
        });
        const payload = await response.json();
        if (!response.ok) throw new Error(payload.error || 'Failed to save eMAR record');
        newRecord = {
          ...draftRecord,
          ...payload,
          medicationName: payload.medicationName || selectedMedication.medicationName,
          dosageGiven: payload.dosageGiven || selectedMedication.dosage,
        };
        await refetchProductionSafety();
      } catch (saveError) {
        setRecordError(saveError instanceof Error ? saveError.message : 'Failed to save eMAR record');
        setIsSavingRecord(false);
        return;
      }
    }

    if (import.meta.env.VITE_LOCAL_DATA_MODE === 'true') {
      setAdministrations((records) => {
        const nextRecords = [newRecord, ...records];
        setLocalStorage(MAR_RECORDS_KEY, nextRecords);
        return nextRecords;
      });
    }

    if (form.status !== 'GIVEN' && import.meta.env.VITE_LOCAL_DATA_MODE === 'true') {
      const reason = form.status === 'REFUSED'
        ? form.refusalReason || 'Client refused medication support.'
        : form.status === 'MISSED'
          ? 'Medication was not confirmed during the expected visit window.'
          : 'Medication was held and requires manager or clinical review.';
      const escalation: MedicationEscalation = {
        id: `emar-escalation-${Date.now()}`,
        medicationId: selectedMedication.id,
        medicationName: selectedMedication.medicationName,
        clientId,
        reason,
        priority: form.status === 'MISSED' ? 'HIGH' : 'MEDIUM',
        createdAt: now.toISOString(),
        dueAt: new Date(now.getTime() + 30 * 60 * 1000).toISOString(),
        owner: 'Registered manager',
        status: 'PENDING_APPROVAL',
      };
      setEscalations((items) => {
        const nextItems = [escalation, ...items];
        setLocalStorage(MAR_ESCALATIONS_KEY, nextItems);
        return nextItems;
      });

      const approval: HumanApprovalItem = {
        id: `emar-approval-${Date.now()}`,
        medicationId: selectedMedication.id,
        medicationName: selectedMedication.medicationName,
        decision: form.status,
        reason,
        requestedBy: 'Badan Catalin',
        createdAt: now.toISOString(),
        status: 'PENDING_APPROVAL',
      };
      setApprovals((items) => {
        const nextItems = [approval, ...items];
        setLocalStorage(MAR_APPROVALS_KEY, nextItems);
        return nextItems;
      });
    }

    setSelectedMedicationId(null);
    setForm(initialForm);
    setIsSavingRecord(false);
    setActiveTab(form.status === 'GIVEN' ? 'audit' : 'escalations');
  };

  const reviewApproval = (id: string, status: ApprovalStatus) => {
    const now = new Date().toISOString();
    setApprovals((items) => {
      const nextItems = items.map((item) =>
        item.id === id
          ? { ...item, status, reviewedBy: 'Marius Dorobat', reviewedAt: now }
          : item
      );
      setLocalStorage(MAR_APPROVALS_KEY, nextItems);
      return nextItems;
    });
  };

  const closeEscalation = (id: string) => {
    setEscalations((items) => {
      const nextItems = items.map((item) =>
        item.id === id ? { ...item, status: 'REVIEWED' as ApprovalStatus } : item
      );
      setLocalStorage(MAR_ESCALATIONS_KEY, nextItems);
      return nextItems;
    });
  };

  const completePrnFollowUp = async (recordId: string) => {
    const draft = followUpDrafts[recordId];
    if (!draft?.effectivenessReview.trim() || !draft.signatureName.trim()) return;

    if (import.meta.env.VITE_LOCAL_DATA_MODE !== 'true') {
      const response = await fetch(`/api/emar/${recordId}/prn-follow-up`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(draft),
      });
      if (response.ok) {
        await refetchProductionSafety();
        setFollowUpDrafts((items) => {
          const next = { ...items };
          delete next[recordId];
          return next;
        });
      }
      return;
    }

    setAdministrations((records) => {
      const nextRecords = records.map((record) =>
        record.id === recordId
          ? { ...record, effectivenessReview: draft.effectivenessReview, followUpStatus: 'COMPLETED' as const }
          : record
      );
      setLocalStorage(MAR_RECORDS_KEY, nextRecords);
      return nextRecords;
    });
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-[#6c3a80]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <p className="text-red-800">Failed to load medications. Please try again.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl space-y-6 px-4 py-8 sm:px-6 lg:px-8">
      <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <div className="flex items-center gap-3">
              <div className="rounded-md bg-[#6c3a80]/10 p-3 text-[#6c3a80]">
                <Pill className="h-6 w-6" />
              </div>
              <div>
                <p className="text-sm font-semibold uppercase tracking-wide text-[#6c3a80]">
                  Medication Safety
                </p>
                <h1 className="text-3xl font-bold text-slate-950">eMAR</h1>
              </div>
            </div>
            <p className="mt-4 max-w-3xl text-sm leading-6 text-slate-600">
              Record every medication support event, including PRN reason, refusals, held doses,
              missed doses, follow-up checks and MAR audit evidence.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => downloadMedicationReportPdf(safetyProfile, reportStats, medicationData, marRecords, allEscalations, approvals)}
              className="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-slate-300 px-4 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
            >
              <Download className="h-4 w-4" />
              Download Report PDF
            </button>
            <button
              type="button"
              onClick={() => printMedicationReportPdf(safetyProfile, reportStats, medicationData, marRecords, allEscalations, approvals)}
              className="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-slate-300 px-4 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
            >
              <Printer className="h-4 w-4" />
              Print PDF
            </button>
            <Link
              to={`/clients/${clientId}/medications/add`}
              className="inline-flex h-10 items-center justify-center rounded-md bg-[#6c3a80] px-4 text-sm font-semibold text-white transition hover:bg-[#5e3474]"
            >
              Add Medication
            </Link>
          </div>
        </div>
      </section>

      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <MetricCard label="PRN protocols" value={String(prnCount)} icon={<ClipboardCheck className="h-5 w-5" />} />
        <MetricCard label="Overdue doses" value={String(overdueCount)} icon={<Clock className="h-5 w-5" />} tone={overdueCount > 0 ? 'warning' : 'default'} />
        <MetricCard label="Warnings" value={String(warningCount)} icon={<AlertTriangle className="h-5 w-5" />} tone={warningCount > 0 ? 'warning' : 'default'} />
        <MetricCard label="High-risk checks" value={String(highRiskCount)} icon={<UserCheck className="h-5 w-5" />} tone={highRiskCount > 0 ? 'warning' : 'default'} />
        <MetricCard label="Low stock" value={String(lowStockCount)} icon={<Pill className="h-5 w-5" />} tone={lowStockCount > 0 ? 'danger' : 'default'} />
        <MetricCard label="Missed records" value={String(missedCount)} icon={<XCircle className="h-5 w-5" />} tone={missedCount > 0 ? 'danger' : 'default'} />
        <MetricCard label="Compliance" value={`${complianceRate}%`} icon={<ShieldCheck className="h-5 w-5" />} tone={complianceRate < 90 ? 'warning' : 'default'} />
        <MetricCard label="Escalations" value={String(openEscalations.length)} icon={<Bell className="h-5 w-5" />} tone={openEscalations.length > 0 ? 'danger' : 'default'} />
        <MetricCard label="Pending approval" value={String(pendingApprovals.length)} icon={<UserCheck className="h-5 w-5" />} tone={pendingApprovals.length > 0 ? 'warning' : 'default'} />
        <MetricCard label="Allergy alerts" value={String(allergyWarningCount)} icon={<AlertTriangle className="h-5 w-5" />} tone={allergyWarningCount > 0 ? 'danger' : 'default'} />
      </section>

      <section className="rounded-lg border border-blue-200 bg-blue-50 p-4">
        <div className="flex gap-3 text-sm leading-6 text-blue-950">
          <ShieldCheck className="mt-0.5 h-5 w-5 flex-shrink-0" />
          <p>
            This eMAR supports NICE/CQC-style record keeping and safety checks. It must be
            clinically reviewed before production medication administration, especially for
            time-critical medicines, covert administration, PRN protocols or dose changes.
            The system records evidence and alerts; sensitive decisions remain under human approval.
          </p>
        </div>
      </section>

      <section className="grid gap-4 lg:grid-cols-2">
        <article className="rounded-lg border border-red-200 bg-white p-5 shadow-sm">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-red-600" />
            <h2 className="text-lg font-bold text-slate-950">Allergy Warnings</h2>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            {safetyProfile.allergies.map((allergy) => (
              <span key={allergy} className="rounded-full bg-red-100 px-3 py-1 text-sm font-semibold text-red-800">
                {allergy}
              </span>
            ))}
          </div>
          <ul className="mt-4 space-y-2 text-sm leading-6 text-slate-700">
            {safetyProfile.highRiskNotes.map((note) => (
              <li key={note} className="flex gap-2">
                <CheckCircle2 className="mt-1 h-4 w-4 flex-shrink-0 text-[#6c3a80]" />
                {note}
              </li>
            ))}
          </ul>
        </article>

        <article className="rounded-lg border border-amber-200 bg-amber-50 p-5 shadow-sm">
          <div className="flex items-center gap-3">
            <UserCheck className="h-5 w-5 text-amber-700" />
            <h2 className="text-lg font-bold text-slate-950">Human Approval Rules</h2>
          </div>
          <p className="mt-3 text-sm leading-6 text-amber-950">
            Refused, held and missed medication records automatically create an escalation and a human approval task.
            The software does not restart, change or recommend a dose by itself.
          </p>
          <div className="mt-4 grid gap-2 text-sm sm:grid-cols-3">
            <div className="rounded-md bg-white p-3">
              <p className="font-semibold text-slate-950">Missed</p>
              <p className="text-slate-600">High priority escalation</p>
            </div>
            <div className="rounded-md bg-white p-3">
              <p className="font-semibold text-slate-950">Refused</p>
              <p className="text-slate-600">Reason and review required</p>
            </div>
            <div className="rounded-md bg-white p-3">
              <p className="font-semibold text-slate-950">Held</p>
              <p className="text-slate-600">Manager review required</p>
            </div>
          </div>
        </article>
      </section>

      <div className="border-b border-slate-200">
        <nav className="-mb-px flex flex-wrap gap-6">
          {[
            { id: 'board', label: 'eMAR Board' },
            { id: 'rounds', label: 'Medication Rounds' },
            { id: 'stock', label: 'Stock Control' },
            { id: 'schedule', label: 'Medication Schedule' },
            { id: 'prn', label: 'PRN Protocols' },
            { id: 'audit', label: 'Full History' },
            { id: 'escalations', label: 'Escalations' },
            { id: 'report', label: 'Compliance Report' },
          ].map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id as MedicationTab)}
              className={`border-b-2 px-1 py-4 text-sm font-semibold transition ${
                activeTab === tab.id
                  ? 'border-[#6c3a80] text-[#6c3a80]'
                  : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === 'board' && (
        <>
          <div className="border-b border-slate-200">
            <nav className="-mb-px flex gap-6">
              {(['ACTIVE', 'ALL', 'DISCONTINUED', 'ON_HOLD'] as const).map((status) => (
                <button
                  key={status}
                  type="button"
                  onClick={() => setFilterStatus(status)}
                  className={`border-b-2 px-1 py-3 text-sm font-medium transition ${
                    filterStatus === status
                      ? 'border-[#6c3a80] text-[#6c3a80]'
                      : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700'
                  }`}
                >
                  {status.replace('_', ' ')}
                </button>
              ))}
            </nav>
          </div>

          {filteredMedications.length === 0 ? (
            <div className="rounded-lg bg-white p-12 text-center shadow">
              <p className="text-lg text-slate-500">No medications found</p>
              <p className="mt-2 text-sm text-slate-400">Add medications to start tracking</p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredMedications.map((medication) => (
                <MedicationCard
                  key={medication.id}
                  medication={medication}
                  onOpenAdministration={openAdministration}
                />
              ))}
            </div>
          )}
        </>
      )}

      {activeTab === 'rounds' && (
        <section className="space-y-4">
          <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-center gap-3">
              <CalendarDays className="h-5 w-5 text-[#6c3a80]" />
              <div>
                <h2 className="text-xl font-bold text-slate-950">Medication Rounds</h2>
                <p className="mt-1 text-sm text-slate-600">
                  Each round groups the medicines due in a visit window and highlights PRN or double-check requirements before recording.
                </p>
              </div>
            </div>
          </div>
          <div className="grid gap-4 xl:grid-cols-2">
            {medicationRounds.map((round) => {
              const roundMedications = medicationData.filter((medication) => round.medicationIds.includes(medication.id));
              return (
                <article key={round.id} className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                      <h3 className="text-lg font-bold text-slate-950">{round.name}</h3>
                      <p className="mt-1 text-sm text-slate-600">
                        Window: {round.windowStart} - {round.windowEnd}
                      </p>
                    </div>
                    <span className={`rounded-full px-3 py-1 text-xs font-semibold ${getRoundStatusStyle(round.status)}`}>
                      {round.status.replaceAll('_', ' ')}
                    </span>
                  </div>
                  <div className="mt-4 space-y-3">
                    {roundMedications.map((medication) => (
                      <div key={medication.id} className="rounded-md border border-slate-200 p-3">
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                          <div>
                            <p className="font-semibold text-slate-950">{medication.medicationName}</p>
                            <p className="text-sm text-slate-600">{medication.dosage} | {medication.frequency}</p>
                          </div>
                          <button
                            type="button"
                            onClick={() => openAdministration(medication.id, 'GIVEN')}
                            className="rounded-md bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
                          >
                            Record From Round
                          </button>
                        </div>
                        {(medication.highRisk || medication.requiresDoubleCheck || medication.prnProtocol) && (
                          <p className="mt-2 text-sm font-medium text-amber-800">
                            {medication.highRisk || medication.requiresDoubleCheck ? 'Independent double-check required. ' : ''}
                            {medication.prnProtocol ? 'PRN evidence and effectiveness follow-up required.' : ''}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </article>
              );
            })}
          </div>
        </section>
      )}

      {activeTab === 'stock' && (
        <section className="space-y-4">
          <div className="grid gap-4 lg:grid-cols-3">
            {medicationStock.map((stock) => (
              <article key={stock.id} className={`rounded-lg border bg-white p-5 shadow-sm ${stock.status === 'IN_STOCK' ? 'border-slate-200' : 'border-red-200'}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h2 className="text-lg font-bold text-slate-950">{stock.medicationName}</h2>
                    <p className="mt-1 text-sm text-slate-600">
                      Current stock: <span className="font-semibold">{stock.quantity} {stock.unit}</span>
                    </p>
                    <p className="mt-1 text-sm text-slate-600">Reorder level: {stock.reorderLevel} {stock.unit}</p>
                  </div>
                  <span className={`rounded-full px-3 py-1 text-xs font-semibold ${stock.status === 'IN_STOCK' ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}`}>
                    {stock.status.replaceAll('_', ' ')}
                  </span>
                </div>
                <p className="mt-4 text-xs text-slate-500">
                  Last count: {stock.lastCountedAt ? formatDateTime(stock.lastCountedAt) : 'Count required'} by {stock.countedBy || 'Not recorded'}
                </p>
              </article>
            ))}
          </div>

          <article className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-center gap-3">
              <UserCheck className="h-5 w-5 text-[#6c3a80]" />
              <h2 className="text-xl font-bold text-slate-950">Recent Digital Signatures</h2>
            </div>
            <div className="mt-4 overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200 text-sm">
                <thead className="bg-slate-50 text-left text-xs font-semibold uppercase text-slate-500">
                  <tr>
                    <th className="px-4 py-3">Signed at</th>
                    <th className="px-4 py-3">Signed by</th>
                    <th className="px-4 py-3">Role</th>
                    <th className="px-4 py-3">Double-check</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 bg-white">
                  {recentSignatures.length === 0 ? (
                    <tr>
                      <td className="px-4 py-4 text-slate-600" colSpan={4}>No signatures recorded yet.</td>
                    </tr>
                  ) : recentSignatures.map((signature) => (
                    <tr key={signature.id}>
                      <td className="px-4 py-3 text-slate-600">{formatDateTime(signature.signedAt)}</td>
                      <td className="px-4 py-3 font-medium text-slate-950">{signature.signedBy}</td>
                      <td className="px-4 py-3 text-slate-600">{signature.role}</td>
                      <td className="px-4 py-3 text-slate-600">{signature.doubleCheckBy || 'Not required'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </article>
        </section>
      )}

      {activeTab === 'schedule' && (
        <section className="grid gap-4 xl:grid-cols-2">
          {medicationSchedule.map((item) => (
            <article key={item.id} className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <div className="flex items-center gap-3">
                    <CalendarDays className="h-5 w-5 text-[#6c3a80]" />
                    <h2 className="text-lg font-bold text-slate-950">{item.medicationName}</h2>
                  </div>
                  <p className="mt-2 text-sm text-slate-600">
                    {item.dose} | {item.route} | Due {formatDateTime(item.dueAt)}
                  </p>
                  <p className="mt-3 text-sm leading-6 text-slate-700">{item.instruction}</p>
                </div>
                <span className={`rounded-full px-3 py-1 text-xs font-semibold ${getScheduleStatusStyle(item.status)}`}>
                  {item.status}
                </span>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => openAdministration(item.medicationId, 'GIVEN')}
                  className="rounded-md bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
                >
                  Confirm Administration
                </button>
                <button
                  type="button"
                  onClick={() => openAdministration(item.medicationId, 'MISSED')}
                  className="rounded-md border border-red-200 px-4 py-2 text-sm font-semibold text-red-700 hover:bg-red-50"
                >
                  Record Missed
                </button>
              </div>
            </article>
          ))}
        </section>
      )}

      {activeTab === 'prn' && (
        <section className="space-y-4">
          {pendingPrnFollowUps.length > 0 && (
            <article className="rounded-lg border border-amber-200 bg-amber-50 p-5 shadow-sm">
              <div className="flex items-center gap-3">
                <Clock className="h-5 w-5 text-amber-700" />
                <h2 className="text-xl font-bold text-slate-950">Mandatory PRN Effectiveness Follow-Up</h2>
              </div>
              <div className="mt-4 space-y-3">
                {pendingPrnFollowUps.map((record) => {
                  const draft = followUpDrafts[record.id] ?? { effectivenessReview: '', signatureName: '' };
                  return (
                    <div key={record.id} className="rounded-md border border-amber-200 bg-white p-4">
                      <p className="font-semibold text-slate-950">{record.medicationName}</p>
                      <p className="mt-1 text-sm text-slate-600">
                        Due: {record.followUpDueAt ? formatDateTime(record.followUpDueAt) : 'Now'} | Reason: {record.prnReason || 'PRN support'}
                      </p>
                      <div className="mt-3 grid gap-3 sm:grid-cols-[1fr_220px_auto]">
                        <input
                          value={draft.effectivenessReview}
                          onChange={(event) => setFollowUpDrafts((items) => ({ ...items, [record.id]: { ...draft, effectivenessReview: event.target.value } }))}
                          className="h-10 rounded-md border border-slate-300 px-3 text-sm"
                          placeholder="Outcome, pain score change, side effects, action taken"
                        />
                        <input
                          value={draft.signatureName}
                          onChange={(event) => setFollowUpDrafts((items) => ({ ...items, [record.id]: { ...draft, signatureName: event.target.value } }))}
                          className="h-10 rounded-md border border-slate-300 px-3 text-sm"
                          placeholder="Signature"
                        />
                        <button
                          type="button"
                          onClick={() => completePrnFollowUp(record.id)}
                          disabled={!draft.effectivenessReview.trim() || !draft.signatureName.trim()}
                          className="rounded-md bg-[#6c3a80] px-4 py-2 text-sm font-semibold text-white hover:bg-[#5e3474] disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          Complete
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </article>
          )}

          <div className="grid gap-4 xl:grid-cols-2">
          {medicationData.filter((medication) => medication.prnProtocol).map((medication) => (
            <article key={medication.id} className="rounded-lg border border-purple-200 bg-white p-5 shadow-sm">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-lg font-bold text-slate-950">{medication.medicationName}</h2>
                  <p className="mt-1 text-sm text-slate-600">{medication.dosage} | {medication.frequency}</p>
                </div>
                <span className="rounded-full bg-purple-100 px-3 py-1 text-xs font-semibold text-purple-800">
                  PRN
                </span>
              </div>
              <div className="mt-4 rounded-md bg-purple-50 p-4 text-sm leading-6 text-purple-900">
                {medication.prnProtocol}
              </div>
              {medication.maxDailyDose && (
                <p className="mt-3 text-sm font-semibold text-slate-800">
                  Max daily dose: {medication.maxDailyDose}
                </p>
              )}
              <ul className="mt-3 space-y-2 text-sm text-slate-700">
                {(medication.safetyChecks ?? []).map((check) => (
                  <li key={check} className="flex gap-2">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-600" />
                    {check}
                  </li>
                ))}
              </ul>
              <button
                type="button"
                onClick={() => openAdministration(medication.id)}
                className="mt-4 rounded-md bg-[#6c3a80] px-4 py-2 text-sm font-semibold text-white hover:bg-[#5e3474]"
              >
                Record PRN Administration
              </button>
            </article>
          ))}
          </div>
        </section>
      )}

      {activeTab === 'audit' && (
        <section className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-200 p-5">
            <div className="flex items-center gap-3">
              <History className="h-5 w-5 text-[#6c3a80]" />
              <h2 className="text-xl font-bold text-slate-950">Complete Medication History</h2>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50 text-left text-xs font-semibold uppercase text-slate-500">
                <tr>
                  <th className="px-4 py-3">Time</th>
                  <th className="px-4 py-3">Medicine</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Dose</th>
                  <th className="px-4 py-3">Recorded by</th>
                  <th className="px-4 py-3">Evidence</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 bg-white">
                {marRecords.map((record) => (
                  <tr key={record.id}>
                    <td className="px-4 py-3 text-slate-600">{formatDateTime(record.administeredAt)}</td>
                    <td className="px-4 py-3 font-medium text-slate-950">{record.medicationName}</td>
                    <td className="px-4 py-3">
                      <span className={`rounded-full px-2 py-1 text-xs font-semibold ${statusStyles[record.status]}`}>
                        {record.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{record.dosageGiven}</td>
                    <td className="px-4 py-3 text-slate-600">{record.administeredBy}</td>
                    <td className="px-4 py-3 text-slate-600">
                      {record.prnReason && <span>PRN: {record.prnReason}. </span>}
                      {record.painScore && <span>Pain score: {record.painScore}. </span>}
                      {record.refusalReason && <span>Refusal: {record.refusalReason}. </span>}
                      {record.followUpDueAt && <span>Follow-up: {formatDateTime(record.followUpDueAt)}. </span>}
                      {record.followUpStatus === 'PENDING' && <span>PRN effectiveness pending. </span>}
                      {record.requiresDoubleCheck && <span>Double-check: {record.doubleCheckBy || 'recorded'}. </span>}
                      {record.signedBy && <span>Signed by {record.signedBy}. </span>}
                      {record.notes}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {activeTab === 'escalations' && (
        <section className="grid gap-4 xl:grid-cols-2">
          <article className="rounded-lg border border-red-200 bg-white p-5 shadow-sm">
            <div className="flex items-center gap-3">
              <Bell className="h-5 w-5 text-red-600" />
              <h2 className="text-xl font-bold text-slate-950">Medication Escalations</h2>
            </div>
            <div className="mt-4 space-y-3">
              {allEscalations.length === 0 ? (
                <p className="rounded-md bg-slate-50 p-4 text-sm text-slate-600">No medication escalations are open.</p>
              ) : (
                allEscalations.map((item) => (
                  <div key={item.id} className="rounded-md border border-slate-200 p-4">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                      <div>
                        <p className="font-semibold text-slate-950">{item.medicationName}</p>
                        <p className="mt-1 text-sm leading-6 text-slate-700">{item.reason}</p>
                        <p className="mt-1 text-xs text-slate-500">
                          Owner: {item.owner} | Due: {formatDateTime(item.dueAt)}
                        </p>
                      </div>
                      <span className={`rounded-full px-3 py-1 text-xs font-semibold ${item.priority === 'HIGH' ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'}`}>
                        {item.priority}
                      </span>
                    </div>
                    {item.status === 'PENDING_APPROVAL' && (
                      <button
                        type="button"
                        onClick={() => closeEscalation(item.id)}
                        className="mt-3 rounded-md bg-[#6c3a80] px-4 py-2 text-sm font-semibold text-white hover:bg-[#5e3474]"
                      >
                        Mark Reviewed
                      </button>
                    )}
                  </div>
                ))
              )}
            </div>
          </article>

          <article className="rounded-lg border border-amber-200 bg-white p-5 shadow-sm">
            <div className="flex items-center gap-3">
              <UserCheck className="h-5 w-5 text-amber-700" />
              <h2 className="text-xl font-bold text-slate-950">Human Approval Queue</h2>
            </div>
            <div className="mt-4 space-y-3">
              {approvals.length === 0 ? (
                <p className="rounded-md bg-slate-50 p-4 text-sm text-slate-600">No sensitive medication decisions are waiting for review.</p>
              ) : (
                approvals.map((item) => (
                  <div key={item.id} className="rounded-md border border-slate-200 p-4">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                      <div>
                        <p className="font-semibold text-slate-950">{item.medicationName}</p>
                        <p className="mt-1 text-sm text-slate-700">
                          Decision: <span className="font-semibold">{item.decision}</span>
                        </p>
                        <p className="mt-1 text-sm leading-6 text-slate-700">{item.reason}</p>
                        <p className="mt-1 text-xs text-slate-500">
                          Requested by {item.requestedBy} on {formatDateTime(item.createdAt)}
                        </p>
                      </div>
                      <span className={`rounded-full px-3 py-1 text-xs font-semibold ${item.status === 'PENDING_APPROVAL' ? 'bg-amber-100 text-amber-800' : item.status === 'REJECTED' ? 'bg-red-100 text-red-800' : 'bg-emerald-100 text-emerald-800'}`}>
                        {item.status.replace('_', ' ')}
                      </span>
                    </div>
                    {item.status === 'PENDING_APPROVAL' && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => reviewApproval(item.id, 'REVIEWED')}
                          className="rounded-md bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
                        >
                          Mark Reviewed
                        </button>
                        <button
                          type="button"
                          onClick={() => reviewApproval(item.id, 'REJECTED')}
                          className="rounded-md border border-red-200 px-4 py-2 text-sm font-semibold text-red-700 hover:bg-red-50"
                        >
                          Reject Record
                        </button>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </article>
        </section>
      )}

      {activeTab === 'report' && (
        <section className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <MetricCard label="Total MAR records" value={String(reportStats.total)} icon={<FileText className="h-5 w-5" />} />
            <MetricCard label="Given records" value={String(reportStats.given)} icon={<CheckCircle2 className="h-5 w-5" />} />
            <MetricCard label="Not confirmed" value={String(reportStats.notConfirmed)} icon={<XCircle className="h-5 w-5" />} tone={reportStats.notConfirmed > 0 ? 'danger' : 'default'} />
            <MetricCard label="Compliance rate" value={`${reportStats.complianceRate}%`} icon={<ShieldCheck className="h-5 w-5" />} tone={reportStats.complianceRate < 90 ? 'warning' : 'default'} />
          </div>

          <article className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="text-xl font-bold text-slate-950">Medication Compliance Report</h2>
                <p className="mt-1 text-sm text-slate-600">
                  Shows confirmed administrations, missed/refused/held records, PRN evidence, allergy warnings and approval status.
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => downloadMedicationReportPdf(safetyProfile, reportStats, medicationData, marRecords, allEscalations, approvals)}
                  className="inline-flex items-center gap-2 rounded-md bg-[#6c3a80] px-4 py-2 text-sm font-semibold text-white hover:bg-[#5e3474]"
                >
                  <Download className="h-4 w-4" />
                  Download PDF
                </button>
                <button
                  type="button"
                  onClick={() => printMedicationReportPdf(safetyProfile, reportStats, medicationData, marRecords, allEscalations, approvals)}
                  className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                >
                  <Printer className="h-4 w-4" />
                  Print PDF
                </button>
              </div>
            </div>

            <div className="mt-5 grid gap-4 lg:grid-cols-3">
              <div className="rounded-md bg-slate-50 p-4">
                <p className="text-sm font-semibold text-slate-950">Medication Notes</p>
                <ul className="mt-3 space-y-2 text-sm text-slate-700">
                  {marRecords.filter((record) => record.notes).slice(0, 5).map((record) => (
                    <li key={record.id}>{record.medicationName}: {record.notes}</li>
                  ))}
                </ul>
              </div>
              <div className="rounded-md bg-slate-50 p-4">
                <p className="text-sm font-semibold text-slate-950">PRN Evidence</p>
                <ul className="mt-3 space-y-2 text-sm text-slate-700">
                  {marRecords.filter((record) => record.prnReason).slice(0, 5).map((record) => (
                    <li key={record.id}>{record.medicationName}: {record.prnReason}, pain score {record.painScore ?? 'N/A'}</li>
                  ))}
                </ul>
              </div>
              <div className="rounded-md bg-slate-50 p-4">
                <p className="text-sm font-semibold text-slate-950">Sensitive Decisions</p>
                <ul className="mt-3 space-y-2 text-sm text-slate-700">
                  {approvals.slice(0, 5).map((item) => (
                    <li key={item.id}>{item.medicationName}: {item.decision} - {item.status.replace('_', ' ')}</li>
                  ))}
                </ul>
              </div>
            </div>
          </article>
        </section>
      )}

      {selectedMedication && (
        <section className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 px-4 py-8">
          <div className="max-h-full w-full max-w-3xl overflow-y-auto rounded-lg bg-white shadow-2xl">
            <div className="border-b border-slate-200 p-5">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-sm font-semibold uppercase tracking-wide text-[#6c3a80]">eMAR Record</p>
                  <h2 className="mt-1 text-2xl font-bold text-slate-950">{selectedMedication.medicationName}</h2>
                  <p className="mt-1 text-sm text-slate-600">{selectedMedication.dosage} | {selectedMedication.route} | {selectedMedication.frequency}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedMedicationId(null)}
                  className="rounded-md border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Close
                </button>
              </div>
            </div>

            <div className="space-y-5 p-5">
              <div className="grid gap-3 sm:grid-cols-4">
                {(['GIVEN', 'REFUSED', 'HELD', 'MISSED'] as AdministrationStatus[]).map((status) => (
                  <button
                    key={status}
                    type="button"
                    onClick={() => setForm((current) => ({ ...current, status }))}
                    className={`rounded-md border px-3 py-2 text-sm font-semibold ${
                      form.status === status
                        ? 'border-[#6c3a80] bg-[#6c3a80] text-white'
                        : 'border-slate-300 text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    {status}
                  </button>
                ))}
              </div>

              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                <h3 className="font-semibold text-slate-950">Safety Checklist</h3>
                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  {safetyFields.map((field) => (
                    <label key={field} className="flex items-center gap-3 rounded-md bg-white p-3 text-sm text-slate-700">
                      <input
                        type="checkbox"
                        checked={form[field]}
                        onChange={(event) => setForm((current) => ({ ...current, [field]: event.target.checked }))}
                        className="h-4 w-4 rounded border-slate-300 text-[#6c3a80] focus:ring-[#6c3a80]"
                      />
                      {safetyLabels[field]}
                    </label>
                  ))}
                </div>
              </div>

              {selectedMedication.prnProtocol && form.status === 'GIVEN' && (
                <div className="rounded-lg border border-purple-200 bg-purple-50 p-4">
                  <h3 className="font-semibold text-purple-950">PRN Protocol Evidence</h3>
                  <p className="mt-1 text-sm leading-6 text-purple-900">{selectedMedication.prnProtocol}</p>
                  <div className="mt-4 grid gap-4 sm:grid-cols-3">
                    <Field label="Reason for PRN">
                      <input
                        value={form.prnReason}
                        onChange={(event) => setForm((current) => ({ ...current, prnReason: event.target.value }))}
                        className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm"
                        placeholder="Pain, fever, anxiety..."
                      />
                    </Field>
                    <Field label="Pain score 0-10">
                      <input
                        value={form.painScore}
                        onChange={(event) => setForm((current) => ({ ...current, painScore: event.target.value }))}
                        className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm"
                        placeholder="0-10"
                      />
                    </Field>
                    <Field label="Temperature">
                      <div className="relative">
                        <Thermometer className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                        <input
                          value={form.bodyTemperature}
                          onChange={(event) => setForm((current) => ({ ...current, bodyTemperature: event.target.value }))}
                          className="h-10 w-full rounded-md border border-slate-300 pl-9 pr-3 text-sm"
                          placeholder="Optional"
                        />
                      </div>
                    </Field>
                  </div>
                </div>
              )}

              {form.status === 'REFUSED' && (
                <Field label="Refusal reason">
                  <textarea
                    value={form.refusalReason}
                    onChange={(event) => setForm((current) => ({ ...current, refusalReason: event.target.value }))}
                    rows={3}
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                    placeholder="Record exactly why the client refused and who was informed."
                  />
                </Field>
              )}

              {doubleCheckRequired && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                  <h3 className="font-semibold text-amber-950">High-Risk Medication Double-Check</h3>
                  <p className="mt-1 text-sm leading-6 text-amber-900">
                    A second competent person must verify the medicine, dose, route, time and client before this record can be saved.
                  </p>
                  <div className="mt-4 grid gap-4 sm:grid-cols-2">
                    <Field label="Checked by">
                      <input
                        value={form.doubleCheckBy}
                        onChange={(event) => setForm((current) => ({ ...current, doubleCheckBy: event.target.value }))}
                        className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm"
                        placeholder="Second checker name"
                      />
                    </Field>
                    <Field label="Second checker signature">
                      <input
                        value={form.doubleCheckSignature}
                        onChange={(event) => setForm((current) => ({ ...current, doubleCheckSignature: event.target.value }))}
                        className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm"
                        placeholder="Type full name as signature"
                      />
                    </Field>
                  </div>
                </div>
              )}

              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Effectiveness review">
                  <input
                    value={form.effectivenessReview}
                    onChange={(event) => setForm((current) => ({ ...current, effectivenessReview: event.target.value }))}
                    className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm"
                    placeholder="Review after 60 minutes, no adverse reaction..."
                  />
                </Field>
                <Field label="Notes">
                  <input
                    value={form.notes}
                    onChange={(event) => setForm((current) => ({ ...current, notes: event.target.value }))}
                    className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm"
                    placeholder="Optional notes"
                  />
                </Field>
              </div>

              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                <h3 className="font-semibold text-slate-950">Digital Signature</h3>
                <div className="mt-4 grid gap-4 sm:grid-cols-3">
                  <Field label="Signed by">
                    <input
                      value={form.signatureName}
                      onChange={(event) => setForm((current) => ({ ...current, signatureName: event.target.value }))}
                      className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm"
                      placeholder="Full name"
                    />
                  </Field>
                  <Field label="Role">
                    <select
                      value={form.signatureRole}
                      onChange={(event) => setForm((current) => ({ ...current, signatureRole: event.target.value }))}
                      className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm"
                    >
                      <option>Carer</option>
                      <option>Senior Carer</option>
                      <option>Manager</option>
                      <option>Admin</option>
                    </select>
                  </Field>
                  {selectedMedication.prnProtocol && form.status === 'GIVEN' && (
                    <Field label="PRN follow-up due">
                      <select
                        value={form.followUpDueMinutes}
                        onChange={(event) => setForm((current) => ({ ...current, followUpDueMinutes: event.target.value }))}
                        className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm"
                      >
                        <option value="30">30 minutes</option>
                        <option value="60">60 minutes</option>
                        <option value="120">2 hours</option>
                      </select>
                    </Field>
                  )}
                </div>
                <label className="mt-4 flex items-start gap-3 rounded-md bg-white p-3 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={form.signatureDeclaration}
                    onChange={(event) => setForm((current) => ({ ...current, signatureDeclaration: event.target.checked }))}
                    className="mt-0.5 h-4 w-4 rounded border-slate-300 text-[#6c3a80] focus:ring-[#6c3a80]"
                  />
                  I confirm this eMAR record is accurate, was completed by me, and any required escalation or follow-up has been recorded.
                </label>
              </div>

              {form.status !== 'GIVEN' && (
                <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm leading-6 text-amber-950">
                  This is a sensitive medication record. Saving it will automatically create an escalation and a human approval task for manager review.
                </div>
              )}

              {!canRecord && (
                <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                  Complete all safety checks, PRN evidence, double-check fields, refusal reason and digital signature where required before saving this MAR record.
                </div>
              )}

              {recordError && (
                <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                  {recordError}
                </div>
              )}

              <button
                type="button"
                onClick={recordAdministration}
                disabled={!canRecord || isSavingRecord}
                className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-md bg-[#6c3a80] px-4 text-sm font-semibold text-white transition hover:bg-[#5e3474] disabled:cursor-not-allowed disabled:opacity-50"
              >
                <ClipboardCheck className="h-4 w-4" />
                {isSavingRecord ? 'Saving eMAR Record...' : 'Save MAR Record'}
              </button>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

function MedicationCard({
  medication,
  onOpenAdministration,
}: {
  medication: Medication;
  onOpenAdministration: (medicationId: string, status?: AdministrationStatus) => void;
}) {
  const overdue = isOverdue(medication);
  const refillNeeded = needsRefill(medication);

  return (
    <article className={`overflow-hidden rounded-lg bg-white shadow ${overdue ? 'border-l-4 border-red-500' : 'border border-slate-200'}`}>
      <div className="p-6">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex flex-1 items-start gap-4">
            <div className="rounded-md bg-[#6c3a80]/10 p-3 text-[#6c3a80]">
              <Pill className="h-6 w-6" />
            </div>
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="text-lg font-semibold text-slate-950">{medication.medicationName}</h3>
                <span className={`rounded-full px-2 py-1 text-xs font-medium ${getMedicationStatusColor(medication.status)}`}>
                  {medication.status}
                </span>
                {medication.prnProtocol && <span className="rounded-full bg-purple-100 px-2 py-1 text-xs font-medium text-purple-800">PRN</span>}
                {(medication.highRisk || medication.requiresDoubleCheck) && <span className="rounded-full bg-amber-100 px-2 py-1 text-xs font-medium text-amber-800">Double-check</span>}
                {refillNeeded && <span className="rounded-full bg-orange-100 px-2 py-1 text-xs font-medium text-orange-800">Refill needed</span>}
                {overdue && <span className="rounded-full bg-red-100 px-2 py-1 text-xs font-medium text-red-800">Overdue</span>}
              </div>

              {medication.genericName && (
                <p className="mt-1 text-sm text-slate-600">Generic: {medication.genericName}</p>
              )}

              <div className="mt-4 grid grid-cols-2 gap-4 text-sm md:grid-cols-4">
                <Detail label="Dosage" value={medication.dosage} />
                <Detail label="Frequency" value={medication.frequency} />
                <Detail label="Route" value={medication.route} />
                <Detail label="Prescribed by" value={medication.prescribedBy} />
              </div>

              {medication.instructions && (
                <div className="mt-4 rounded-md bg-blue-50 p-3">
                  <p className="text-sm text-slate-700">
                    <span className="font-medium">Instructions:</span> {medication.instructions}
                  </p>
                </div>
              )}

              {medication.warnings && medication.warnings.length > 0 && (
                <div className="mt-3 rounded-md border border-yellow-200 bg-yellow-50 p-3">
                  <p className="text-sm font-medium text-yellow-900">Warnings</p>
                  <ul className="mt-1 list-disc space-y-1 pl-5 text-sm text-yellow-800">
                    {medication.warnings.map((warning) => (
                      <li key={warning}>{warning}</li>
                    ))}
                  </ul>
                </div>
              )}

              {(medication.prnProtocol || medication.safetyChecks?.length) && (
                <div className="mt-3 rounded-md border border-purple-200 bg-purple-50 p-3">
                  <p className="text-sm font-semibold text-purple-950">Medication safety</p>
                  {medication.prnProtocol && (
                    <p className="mt-1 text-sm text-purple-900">
                      <span className="font-medium">PRN protocol:</span> {medication.prnProtocol}
                    </p>
                  )}
                  {medication.maxDailyDose && (
                    <p className="mt-1 text-sm text-purple-900">
                      <span className="font-medium">Max daily dose:</span> {medication.maxDailyDose}
                    </p>
                  )}
                  {medication.safetyChecks && medication.safetyChecks.length > 0 && (
                    <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-purple-900">
                      {medication.safetyChecks.map((check) => (
                        <li key={check}>{check}</li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              <div className="mt-4 grid gap-2 text-sm text-slate-600 sm:grid-cols-2">
                {medication.lastAdministered && (
                  <p>Last given: {formatDateTime(medication.lastAdministered)}</p>
                )}
                {medication.nextScheduledTime && (
                  <p className={overdue ? 'font-medium text-red-600' : 'text-slate-600'}>
                    Next due: {formatDateTime(medication.nextScheduledTime)}
                    {overdue ? ' (overdue)' : ''}
                  </p>
                )}
              </div>
            </div>
          </div>

          {medication.status === 'ACTIVE' && (
            <div className="grid gap-2 sm:grid-cols-2 lg:w-44 lg:grid-cols-1">
              <button
                type="button"
                onClick={() => onOpenAdministration(medication.id, 'GIVEN')}
                className="rounded-md bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-emerald-700"
              >
                Record Given
              </button>
              <button
                type="button"
                onClick={() => onOpenAdministration(medication.id, 'REFUSED')}
                className="rounded-md border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
              >
                Refused
              </button>
              <button
                type="button"
                onClick={() => onOpenAdministration(medication.id, 'HELD')}
                className="rounded-md border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
              >
                Held
              </button>
              <button
                type="button"
                onClick={() => onOpenAdministration(medication.id, 'MISSED')}
                className="rounded-md border border-red-200 px-4 py-2 text-sm font-semibold text-red-700 transition hover:bg-red-50"
              >
                Missed
              </button>
            </div>
          )}
        </div>
      </div>
    </article>
  );
}

function MetricCard({
  label,
  value,
  icon,
  tone = 'default',
}: {
  label: string;
  value: string;
  icon: React.ReactNode;
  tone?: 'default' | 'warning' | 'danger';
}) {
  const colors = {
    default: 'border-slate-200 bg-white text-slate-950',
    warning: 'border-amber-200 bg-amber-50 text-amber-950',
    danger: 'border-red-200 bg-red-50 text-red-950',
  };

  return (
    <div className={`rounded-lg border p-4 shadow-sm ${colors[tone]}`}>
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs font-semibold uppercase text-slate-500">{label}</p>
        <div className="text-[#6c3a80]">{icon}</div>
      </div>
      <p className="mt-2 text-3xl font-bold">{value}</p>
    </div>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-slate-500">{label}</p>
      <p className="font-medium text-slate-950">{value}</p>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-slate-800">{label}</span>
      {children}
    </label>
  );
}

function buildMedicationRounds(medications: Medication[]): MedicationRound[] {
  return medications
    .filter((medication) => medication.status === 'ACTIVE')
    .map((medication, index) => ({
      id: `round-${medication.id}`,
      clientId: medication.clientId,
      name: medication.prnProtocol ? 'PRN medication round' : index % 2 === 0 ? 'Morning medication round' : 'Evening medication round',
      windowStart: medication.prnProtocol ? 'AS_REQUIRED' : index % 2 === 0 ? '08:00' : '18:00',
      windowEnd: medication.prnProtocol ? 'AS_REQUIRED' : index % 2 === 0 ? '10:00' : '20:00',
      medicationIds: [medication.id],
      status: medication.highRisk || medication.requiresDoubleCheck
        ? 'DOUBLE_CHECK_REQUIRED'
        : medication.prnProtocol
          ? 'PRN'
          : 'DUE',
    }));
}

function buildMedicationStock(medications: Medication[]): MedicationStockItem[] {
  return medications.map((medication, index) => ({
    id: `stock-${medication.id}`,
    medicationId: medication.id,
    clientId: medication.clientId,
    medicationName: medication.medicationName,
    quantity: medication.refillsRemaining !== undefined ? medication.refillsRemaining * 7 : index === 0 ? 14 : 5,
    unit: medication.route === 'INHALATION' ? 'inhaler' : 'tablet',
    reorderLevel: medication.route === 'INHALATION' ? 1 : 10,
    status: medication.refillsRemaining !== undefined && medication.refillsRemaining <= 1 ? 'LOW_STOCK' : 'IN_STOCK',
  }));
}

function buildMedicationSchedule(
  medications: Medication[],
  records: MedicationAdministration[]
): MedicationScheduleItem[] {
  return medications
    .filter((medication) => medication.status === 'ACTIVE')
    .map((medication) => {
      const dueAt = medication.nextScheduledTime ?? dateHoursFromNow(2);
      const latestRecord = records.find((record) => record.medicationId === medication.id);
      const isConfirmedToday = Boolean(
        latestRecord &&
        latestRecord.status === 'GIVEN' &&
        new Date(latestRecord.administeredAt).toDateString() === new Date().toDateString()
      );
      const status: ScheduleStatus = medication.prnProtocol
        ? 'PRN'
        : isConfirmedToday
          ? 'CONFIRMED'
          : new Date(dueAt) < new Date()
            ? 'OVERDUE'
            : 'DUE';

      return {
        id: `schedule-${medication.id}`,
        medicationId: medication.id,
        medicationName: medication.medicationName,
        dose: medication.dosage,
        route: medication.route,
        dueAt,
        status,
        instruction: medication.instructions ?? 'Confirm MAR chart and record support outcome.',
      };
    })
    .sort((a, b) => new Date(a.dueAt).getTime() - new Date(b.dueAt).getTime());
}

function getRoundStatusStyle(status: MedicationRound['status']) {
  switch (status) {
    case 'DOUBLE_CHECK_REQUIRED':
      return 'bg-amber-100 text-amber-800';
    case 'COMPLETED':
      return 'bg-emerald-100 text-emerald-800';
    case 'MISSED':
      return 'bg-red-100 text-red-800';
    case 'PRN':
      return 'bg-purple-100 text-purple-800';
    case 'DUE':
    default:
      return 'bg-blue-100 text-blue-800';
  }
}

function getScheduleStatusStyle(status: ScheduleStatus) {
  switch (status) {
    case 'CONFIRMED':
      return 'bg-emerald-100 text-emerald-800';
    case 'OVERDUE':
      return 'bg-red-100 text-red-800';
    case 'PRN':
      return 'bg-purple-100 text-purple-800';
    case 'DUE':
    default:
      return 'bg-blue-100 text-blue-800';
  }
}

function calculateMedicationCompliance(records: MedicationAdministration[]) {
  if (records.length === 0) return 100;
  const given = records.filter((record) => record.status === 'GIVEN').length;
  return Math.round((given / records.length) * 100);
}

function buildMedicationReportStats(
  records: MedicationAdministration[],
  medications: Medication[],
  escalations: MedicationEscalation[],
  approvals: HumanApprovalItem[]
) {
  const given = records.filter((record) => record.status === 'GIVEN').length;
  const refused = records.filter((record) => record.status === 'REFUSED').length;
  const held = records.filter((record) => record.status === 'HELD').length;
  const missed = records.filter((record) => record.status === 'MISSED').length;

  return {
    total: records.length,
    activeMedications: medications.filter((medication) => medication.status === 'ACTIVE').length,
    given,
    refused,
    held,
    missed,
    notConfirmed: refused + held + missed,
    prnProtocols: medications.filter((medication) => Boolean(medication.prnProtocol)).length,
    warnings: medications.reduce((total, medication) => total + (medication.warnings?.length ?? 0), 0),
    openEscalations: escalations.filter((item) => item.status === 'PENDING_APPROVAL').length,
    pendingApprovals: approvals.filter((item) => item.status === 'PENDING_APPROVAL').length,
    complianceRate: calculateMedicationCompliance(records),
  };
}

function sanitizePdfText(value: string): string {
  return value
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^\x20-\x7E\n\r\t]/g, ' ')
    .replace(/[()\\]/g, (match) => `\\${match}`)
    .trim();
}

function wrapPdfText(text: string, maxLength: number): string[] {
  const words = text.split(/\s+/);
  const lines: string[] = [];
  let current = '';

  words.forEach((word) => {
    const next = current ? `${current} ${word}` : word;
    if (next.length > maxLength && current) {
      lines.push(current);
      current = word;
    } else {
      current = next;
    }
  });

  if (current) lines.push(current);
  return lines;
}

function buildMedicationReportPdf(
  profile: MedicationSafetyProfile,
  stats: ReturnType<typeof buildMedicationReportStats>,
  medications: Medication[],
  records: MedicationAdministration[],
  escalations: MedicationEscalation[],
  approvals: HumanApprovalItem[]
): Blob {
  const rawLines = [
    'eMAR and Medication Safety Report',
    'FUCHSIA HOME CARE CATALIN',
    'Yarmouth Business Park, Suffolk Rd, Great Yarmouth, NR31 0LN',
    'Tel: 01493738488 | On Call: 07443145811 | Email: lowestoft@fuchsiahomecare.co.uk',
    '',
    `Client: ${profile.clientName}`,
    `Allergies: ${profile.allergies.join(', ')}`,
    `Generated: ${new Date().toLocaleString('en-GB')}`,
    '',
    'SUMMARY',
    `Active medications: ${stats.activeMedications}`,
    `MAR records: ${stats.total}`,
    `Given: ${stats.given}`,
    `Refused: ${stats.refused}`,
    `Held: ${stats.held}`,
    `Missed: ${stats.missed}`,
    `Compliance rate: ${stats.complianceRate}%`,
    `Open escalations: ${stats.openEscalations}`,
    `Pending human approvals: ${stats.pendingApprovals}`,
    '',
    'MEDICATION SCHEDULE',
    ...medications.flatMap((medication) =>
      wrapPdfText(
        `${medication.medicationName} | ${medication.dosage} | ${medication.route} | ${medication.frequency} | ${medication.status} | ${medication.instructions ?? 'No instructions recorded'}`,
        92
      )
    ),
    '',
    'RECENT MAR HISTORY',
    ...records.slice(0, 20).flatMap((record) =>
      wrapPdfText(
        `${formatDateTime(record.administeredAt)} | ${record.medicationName} | ${record.status} | ${record.dosageGiven} | Recorded by ${record.administeredBy} | ${record.notes ?? record.refusalReason ?? record.prnReason ?? 'No note'}`,
        92
      )
    ),
    '',
    'ESCALATIONS',
    ...(escalations.length
      ? escalations.flatMap((item) =>
          wrapPdfText(`${item.medicationName} | ${item.priority} | ${item.status} | ${item.reason}`, 92)
        )
      : ['No escalations recorded.']),
    '',
    'HUMAN APPROVAL QUEUE',
    ...(approvals.length
      ? approvals.flatMap((item) =>
          wrapPdfText(`${item.medicationName} | ${item.decision} | ${item.status} | ${item.reason}`, 92)
        )
      : ['No human approvals recorded.']),
    '',
    'Safety note: This report records evidence and alerts. It does not replace clinical judgement, GP instructions, pharmacy advice or registered manager approval.',
  ].filter(Boolean).map(sanitizePdfText);

  const linesPerPage = 56;
  const pages: string[][] = [];
  for (let index = 0; index < rawLines.length; index += linesPerPage) {
    pages.push(rawLines.slice(index, index + linesPerPage));
  }

  const objects: string[] = [];
  const addObject = (body: string) => {
    objects.push(body);
    return objects.length;
  };

  const catalogId = addObject('<< /Type /Catalog /Pages 2 0 R >>');
  const pagesId = addObject('');
  const fontId = addObject('<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>');
  const pageIds: number[] = [];

  pages.forEach((pageLines) => {
    const content = [
      'BT',
      '/F1 10 Tf',
      '48 790 Td',
      '14 TL',
      ...pageLines.map((line, lineIndex) =>
        lineIndex === 0 ? `/F1 16 Tf (${line}) Tj /F1 10 Tf T*` : `(${line}) Tj T*`
      ),
      'ET',
    ].join('\n');
    const contentId = addObject(`<< /Length ${content.length} >>\nstream\n${content}\nendstream`);
    const pageId = addObject(
      `<< /Type /Page /Parent ${pagesId} 0 R /MediaBox [0 0 595 842] /Resources << /Font << /F1 ${fontId} 0 R >> >> /Contents ${contentId} 0 R >>`
    );
    pageIds.push(pageId);
  });

  objects[pagesId - 1] = `<< /Type /Pages /Kids [${pageIds.map((id) => `${id} 0 R`).join(' ')}] /Count ${pageIds.length} >>`;

  const chunks = ['%PDF-1.4\n'];
  const offsets = [0];
  objects.forEach((object, index) => {
    offsets.push(chunks.join('').length);
    chunks.push(`${index + 1} 0 obj\n${object}\nendobj\n`);
  });
  const xrefOffset = chunks.join('').length;
  chunks.push(`xref\n0 ${objects.length + 1}\n`);
  chunks.push('0000000000 65535 f \n');
  offsets.slice(1).forEach((offset) => {
    chunks.push(`${String(offset).padStart(10, '0')} 00000 n \n`);
  });
  chunks.push(`trailer\n<< /Size ${objects.length + 1} /Root ${catalogId} 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`);

  return new Blob(chunks, { type: 'application/pdf' });
}

function downloadMedicationReportPdf(
  profile: MedicationSafetyProfile,
  stats: ReturnType<typeof buildMedicationReportStats>,
  medications: Medication[],
  records: MedicationAdministration[],
  escalations: MedicationEscalation[],
  approvals: HumanApprovalItem[]
): void {
  const url = URL.createObjectURL(buildMedicationReportPdf(profile, stats, medications, records, escalations, approvals));
  const link = document.createElement('a');
  link.href = url;
  link.download = `emar-medication-safety-${new Date().toISOString().slice(0, 10)}.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function printMedicationReportPdf(
  profile: MedicationSafetyProfile,
  stats: ReturnType<typeof buildMedicationReportStats>,
  medications: Medication[],
  records: MedicationAdministration[],
  escalations: MedicationEscalation[],
  approvals: HumanApprovalItem[]
): boolean {
  const url = URL.createObjectURL(buildMedicationReportPdf(profile, stats, medications, records, escalations, approvals));
  const pdfWindow = window.open(url, '_blank', 'noopener,noreferrer');
  if (!pdfWindow) {
    URL.revokeObjectURL(url);
    return false;
  }
  window.setTimeout(() => {
    pdfWindow.print();
    URL.revokeObjectURL(url);
  }, 700);
  return true;
}

function getMedicationStatusColor(status: MedicationStatus) {
  switch (status) {
    case 'ACTIVE':
      return 'bg-green-100 text-green-800';
    case 'DISCONTINUED':
      return 'bg-gray-100 text-gray-800';
    case 'ON_HOLD':
      return 'bg-yellow-100 text-yellow-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
}

function isOverdue(medication: Medication) {
  if (!medication.nextScheduledTime || medication.status !== 'ACTIVE') return false;
  return new Date(medication.nextScheduledTime) < new Date();
}

function needsRefill(medication: Medication) {
  return medication.refillsRemaining !== undefined && medication.refillsRemaining <= 1;
}

function dateHoursFromNow(hours: number) {
  return new Date(Date.now() + hours * 60 * 60 * 1000).toISOString();
}

function formatDateTime(value: string) {
  return new Date(value).toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}
